import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Download,
  X,
  HardDrive,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Trash2,
  RefreshCw,
} from 'lucide-react';
import { Button } from '../ui/button';
import { useAudio } from '../../context/AudioContext';
import { audioDownload } from '../../audio/audioDownload';
import { audioStorage } from '../../audio/audioStorage';
import { quranApi } from '../../services/api';

const fmtMB = (bytes) => bytes ? `${(bytes / (1024 * 1024)).toFixed(0)} Mo` : '—';

const titleFor = (scope, surahName) => {
  if (!scope) return '';
  if (scope.scopeType === 'surah') return `Sourate ${scope.surahNumber}${surahName ? ' — ' + surahName : ''}`;
  if (scope.scopeType === 'hizb')  return `Ḥizb ${scope.hizbNumber}`;
  return 'Coran complet';
};

const messageFor = (scope) => {
  if (!scope) return '';
  if (scope.scopeType === 'surah') {
    return scope._fromAyah
      ? "Ce verset appartient à cette sourate. Télécharge la sourate complète pour activer l'audio."
      : "Pour écouter cette sourate, télécharge son audio sur ton appareil.";
  }
  if (scope.scopeType === 'hizb') return "Pour écouter ce ḥizb, télécharge son audio sur ton appareil.";
  return "Pour écouter tout le Coran avec ce récitateur, télécharge l'audio complet.";
};

export const AudioDownloadPrompt = () => {
  const {
    state, clearNeedsDownload, reciters, setReciter,
    playSurah, playHizb, playFullQuran,
  } = useAudio();
  const scope = state.needsDownload;
  const [busy, setBusy] = useState(false);
  const [downloadId, setDownloadId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [storageInfo, setStorageInfo] = useState(null);
  const [surahName, setSurahName] = useState(null);
  const [estCount, setEstCount] = useState(null);
  // Local override of the chosen reciter for THIS download. Defaults to the
  // user's preferred reciter but lets them switch at the moment of download.
  const [reciterChoice, setReciterChoice] = useState(state.reciterId);

  useEffect(() => {
    setReciterChoice(state.reciterId);
  }, [state.reciterId, scope]);

  useEffect(() => {
    if (!scope) {
      setDownloadId(null); setProgress(null);
      return;
    }
    audioStorage.getStorageUsage().then(setStorageInfo);
    if (scope.scopeType === 'surah') {
      quranApi.getSurah(scope.surahNumber).then((r) => {
        setSurahName(r.data?.name_french || r.data?.name_english);
        setEstCount(r.data?.verses_count || r.data?.total_ayahs);
      }).catch(() => {});
    } else if (scope.scopeType === 'hizb') {
      setEstCount(110); // average; finalized once download starts
    } else {
      setEstCount(6236);
    }
  }, [scope]);

  useEffect(() => {
    if (!downloadId) return;
    const unsub = audioDownload.subscribe(downloadId, setProgress);
    return () => unsub();
  }, [downloadId]);

  if (!scope) return null;
  const estBytes = audioDownload.estimateDownloadSizeBytes(estCount || 0);

  const startDownload = async () => {
    setBusy(true);
    try {
      // Apply the reciter choice (also persists to user prefs)
      if (reciterChoice && reciterChoice !== state.reciterId) {
        await setReciter(reciterChoice);
      }
      const reciter = reciters.find((r) => r.id === reciterChoice) || reciters[0];
      const riwaya = reciter?.riwaya || state.riwaya;
      let id;
      if (scope.scopeType === 'surah')
        id = await audioDownload.downloadSurah(scope.surahNumber, reciterChoice, riwaya);
      else if (scope.scopeType === 'hizb')
        id = await audioDownload.downloadHizb(scope.hizbNumber, reciterChoice, riwaya);
      else
        id = await audioDownload.downloadFullQuran(reciterChoice, riwaya);
      setDownloadId(id);
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (downloadId && progress?.status === 'downloading') {
      audioDownload.cancelDownload(downloadId);
    }
    clearNeedsDownload();
  };

  const closeAndPlay = async () => {
    const target = scope; // capture before we clear
    clearNeedsDownload();
    if (!target) return;
    if (target.scopeType === 'surah') await playSurah(target.surahNumber);
    else if (target.scopeType === 'hizb') await playHizb(target.hizbNumber);
    else await playFullQuran();
  };

  const handleDelete = async () => {
    // Delete failed/partial downloads to allow restart
    try {
      setBusy(true);
      if (scope?.id) {
        await audioStorage.deleteScope(scope.id);
      }
      close();
    } catch (err) {
      console.error('Erreur suppression:', err);
    } finally {
      setBusy(false);
    }
  };

  const isDone = progress?.status === 'completed';
  const isFailed = progress?.status === 'failed';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
        onClick={close}
        data-testid="audio-download-prompt"
      >
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          className="bg-surface border border-border rounded-t-2xl sm:rounded-xl w-full sm:max-w-md p-6 m-0 sm:m-4 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-serif text-text-primary">Audio non téléchargé</h3>
              <p className="text-xs text-text-muted mt-1">
                {state.audioMode === 'offline_only' ? 'Mode hors-ligne' : 'Mode hybride'}
              </p>
            </div>
            <button onClick={close} className="text-text-muted hover:text-primary" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="bg-surface-elevated rounded-lg p-4 mb-4">
            <p className="font-medium text-text-primary mb-1">{titleFor(scope, surahName)}</p>
            <p className="text-sm text-text-secondary">{messageFor(scope)}</p>
          </div>

          {/* Reciter selector — let the user choose at download time */}
          <div className="mb-4">
            <label className="text-[11px] uppercase tracking-wider text-text-muted block mb-1.5">
              Récitateur
            </label>
            <select
              value={reciterChoice}
              onChange={(e) => setReciterChoice(e.target.value)}
              disabled={!!progress}
              className="w-full h-10 px-3 rounded-md bg-surface border border-border text-sm text-text-primary disabled:opacity-60"
              data-testid="audio-prompt-reciter-select"
            >
              {reciters.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label} · {r.riwaya} · {r.bitrate} kbps
                </option>
              ))}
            </select>
            <p className="text-[10px] text-text-muted mt-1">
              Tu peux changer de récitateur à chaque téléchargement.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs text-text-muted mb-4">
            <div className="flex items-center gap-1.5">
              <Download className="w-3.5 h-3.5" />
              <span>~{fmtMB(estBytes)} ({estCount || '—'} versets)</span>
            </div>
            {storageInfo?.availableBytes && (
              <div className="flex items-center gap-1.5">
                <HardDrive className="w-3.5 h-3.5" />
                <span>{fmtMB(storageInfo.availableBytes)} libres</span>
              </div>
            )}
          </div>

          {progress && (
            <div className="mb-4">
              <div className="h-2 bg-surface-elevated rounded-full overflow-hidden mb-2">
                <div className="h-full bg-primary transition-all"
                     style={{ width: `${progress.progressPercent || 0}%` }} />
              </div>
              <p className="text-xs text-text-muted flex items-center gap-1.5">
                {progress.status === 'downloading' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {isDone && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
                {isFailed && <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />}
                <span>
                  {progress.downloadedFiles}/{progress.totalFiles} fichiers · {progress.progressPercent}%
                </span>
              </p>
              {progress.error && (
                <p className="text-xs text-amber-400 mt-2 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>{progress.error}</span>
                </p>
              )}
            </div>
          )}

          <div className="flex gap-2 justify-end">
            {isDone ? (
              <Button onClick={closeAndPlay} data-testid="audio-prompt-done" className="bg-primary text-black">
                Fermer et écouter
              </Button>
            ) : isFailed ? (
              <>
                <Button variant="ghost" className="text-red-400 hover:text-red-300"
                        onClick={handleDelete} disabled={busy}
                        data-testid="audio-prompt-delete">
                  <Trash2 className="w-4 h-4 mr-1" /> Supprimer
                </Button>
                <Button
                  onClick={startDownload}
                  disabled={busy}
                  data-testid="audio-prompt-retry"
                  className="bg-primary text-black hover:bg-primary/90"
                >
                  <RefreshCw className="w-4 h-4 mr-1.5" />
                  Réessayer
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={close} data-testid="audio-prompt-cancel">
                  Annuler
                </Button>
                <Button
                  onClick={startDownload}
                  disabled={busy || (progress && progress.status === 'downloading')}
                  data-testid="audio-prompt-download"
                  className="bg-primary text-black hover:bg-primary/90"
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  {progress ? 'Téléchargement…' : 'Télécharger'}
                </Button>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AudioDownloadPrompt;
